import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useUpgrade } from "@/context/UpgradeContext";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FileText,
  Store,
  Mail,
  ArrowRight,
  Sparkles,
  BarChart3,
  BadgeCheck,
  Clock,
  Lock,
  History as HistoryIcon,
} from "lucide-react";

const TOOLS = [
  {
    to: "/patch-notes",
    title: "Steam Patch Notes",
    desc: "Turn raw dev notes and git commits into polished, community-ready changelogs with optional Steam BBCode.",
    icon: FileText,
    tag: "Tool 01",
    free: true,
  },
  {
    to: "/store-copy",
    title: "Steam Store Page Copywriter",
    desc: "Generate a punchy short description and a full 'About This Game' section optimized to convert wishlists.",
    icon: Store,
    tag: "Tool 02",
    free: false,
  },
  {
    to: "/cold-email",
    title: "Streamer & PR Pitcher",
    desc: "Craft catchy subject lines and a high-converting cold email to pitch streamers, creators and press.",
    icon: Mail,
    tag: "Tool 03",
    free: false,
  },
];

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div
    className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-5 transition-colors duration-200 hover:border-teal-500/30"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-zinc-500 mb-3">
      <Icon className="h-4 w-4 text-teal-300" />
      <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{label}</span>
    </div>
    <p className="font-heading text-2xl font-black tracking-tight text-zinc-100 truncate">{value}</p>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const { openUpgrade } = useUpgrade();
  const navigate = useNavigate();
  const [history, setHistory] = useState(null);
  const isPro = !!user?.is_pro;

  useEffect(() => {
    api
      .get("/history")
      .then((res) => setHistory(res.data))
      .catch(() => setHistory([]));
  }, []);

  const firstName = user?.name?.split(" ")[0] || "Indie Dev";
  const total = history?.length ?? 0;
  const toolLabel = (m) =>
    (m || "").includes("Store") ? "Steam Store Page" : (m || "").includes("Patch") ? "Steam Patch Notes" : m || "—";
  const lastTool = history && history.length > 0 ? toolLabel(history[0].module_used) : "—";

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <Layout title="Dashboard" subtitle="Your indie launch command center">
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Welcome banner */}
        <div className="relative overflow-hidden border border-zinc-800 bg-zinc-900/30 rounded-2xl p-6 sm:p-8">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-teal-400/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-teal-300 mb-2">// Welcome back</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-zinc-100" data-testid="welcome-heading">
                Welcome back, {firstName}!
              </h2>
              <p className="text-zinc-400 mt-2 text-sm">{user?.email}</p>
            </div>
            <span
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-teal-500/30 bg-teal-400/10 text-teal-300"
              data-testid="plan-badge"
            >
              <BadgeCheck className="h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-wider capitalize">
                {user?.subscription_status || "free"} Plan
              </span>
            </span>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {history === null ? (
            <>
              <Skeleton className="h-24 w-full bg-zinc-800 rounded-2xl" />
              <Skeleton className="h-24 w-full bg-zinc-800 rounded-2xl" />
              <Skeleton className="h-24 w-full bg-zinc-800 rounded-2xl" />
            </>
          ) : (
            <>
              <StatCard icon={BarChart3} label="Total Generations" value={total} testid="stat-total-generations" />
              <StatCard icon={BadgeCheck} label="Active Plan" value={`${(user?.subscription_status || "free").replace(/^\w/, (c) => c.toUpperCase())}`} testid="stat-active-plan" />
              <StatCard icon={Clock} label="Last Used Tool" value={lastTool} testid="stat-last-tool" />
            </>
          )}
        </div>

        {/* Quick launch tools */}
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Quick Launch</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="quick-launch-grid">
            {TOOLS.map((t) => {
              const locked = !isPro && !t.free;
              return (
              <div
                key={t.to}
                className="group border border-zinc-800 bg-zinc-900/30 rounded-2xl p-6 flex flex-col transition-colors duration-200 hover:border-teal-500/30"
                data-testid={`tool-card-${t.to.replace("/", "")}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-11 w-11 rounded-xl bg-teal-400/10 border border-teal-500/20 flex items-center justify-center text-teal-300">
                    <t.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t.tag}</p>
                    <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-100">{t.title}</h3>
                  </div>
                  {locked && (
                    <span className="flex items-center gap-1 shrink-0 text-[9px] font-mono font-bold uppercase tracking-wider text-teal-300 bg-teal-400/10 border border-teal-500/30 rounded-md px-1.5 py-0.5" data-testid={`card-pro-badge${t.to}`}>
                      <Lock className="h-2.5 w-2.5" /> Pro
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed flex-1">{t.desc}</p>
                {locked ? (
                  <button
                    onClick={() => openUpgrade(`${t.title} is a Pro tool. Upgrade to unlock it.`)}
                    data-testid={`unlock-tool${t.to}`}
                    className="mt-5 inline-flex items-center justify-center gap-2 self-start border border-teal-500/30 text-teal-300 font-semibold py-2.5 px-5 rounded-xl hover:bg-teal-400/10 transition-colors duration-200 active:scale-95"
                  >
                    <Lock className="h-4 w-4" /> Unlock with Pro
                  </button>
                ) : (
                  <button
                    onClick={() => navigate(t.to)}
                    data-testid={`open-tool${t.to}`}
                    className="mt-5 inline-flex items-center justify-center gap-2 self-start bg-teal-400 text-zinc-950 font-semibold py-2.5 px-5 rounded-xl hover:bg-teal-300 transition-colors duration-200 active:scale-95"
                  >
                    <Sparkles className="h-4 w-4" /> Open Tool
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>

        {/* Recent generations */}
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-6" data-testid="recent-generations">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <HistoryIcon className="h-4 w-4 text-teal-300" />
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Recent Generations</p>
            </div>
            <button
              onClick={() => navigate("/history")}
              data-testid="view-all-history-btn"
              className="flex items-center gap-1.5 text-xs border border-zinc-700 text-teal-300 rounded-xl px-3 py-1.5 hover:border-teal-500/40 hover:bg-teal-400/10 transition-colors duration-200"
            >
              View All in History <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {history === null ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full bg-zinc-800 rounded-xl" />
              <Skeleton className="h-12 w-full bg-zinc-800 rounded-xl" />
              <Skeleton className="h-12 w-full bg-zinc-800 rounded-xl" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-zinc-500 py-6 text-center font-mono" data-testid="recent-empty">
              No generations yet. Launch a tool above to get started.
            </p>
          ) : (
            <div className="divide-y divide-zinc-800" data-testid="recent-list">
              {history.slice(0, 5).map((h) => (
                <div key={h.id} className="flex items-center gap-3 py-3" data-testid="recent-item">
                  <div className="h-8 w-8 shrink-0 rounded-xl bg-zinc-800 flex items-center justify-center text-teal-300">
                    {(h.module_used || "").includes("Store") ? <Store className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-100 truncate font-medium">{h.title}</p>
                    <p className="text-[11px] text-zinc-500 font-mono truncate">{h.module_used}</p>
                  </div>
                  <span className="text-[11px] text-zinc-500 font-mono shrink-0">{fmtDate(h.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
